<script setup lang="ts">
/**
 * 🅲🅾🅼🅿🅾🅽🅴🅽🆃 [AddToCart]
 * Модальное окно / экран "Ура! Все готово! Добавить в корзину"
 */

// Store
import { computed, onMounted, ref } from "vue";

// Components
import Modal from "@/components/modal/modal.vue";
import Button from "@/ui/button/button.vue";
// Icons
import AwardIcon from "@/ui/icons/award.vue";
import parseStringPrice from "~/tiw/helpers/parseStringPrice";
import WIDGET_ANALYTICS_EVENTS from "~/tiw/helpers/widgetAnalyticsEvents/widgetAnalyticsEvents";
import { useAnalytics } from "~/tiw/hooks/useAnalytics/useAnalytics";
import useTranslate from "~/tiw/hooks/useTranslate/useTranslate";
import { useHowYouRateDeviceStore } from "~/tiw/stores/howYouRateDevice/howYouRateDevice";
import { useTIWStore } from "~/tiw/stores/tiw/tiw";

import VoucherNumber from "./VoucherNumber.vue";

onMounted(() => {
    useAnalytics(WIDGET_ANALYTICS_EVENTS.VOUCHER_SENT);
});

const TIWStore = useTIWStore();

const thereAreAdditionalCertificates = computed(() => {
    return TIWStore.ADDITIONAL_CERTIFICATE_NUMBERS.length > 0;
});

const additionalCertificateStringEndingForTranslation = computed(() =>
    thereAreAdditionalCertificates.value ? "_withAdditionalCertificates" : "",
);

const HowYouRateDeviceStore = useHowYouRateDeviceStore();
const devicePrice = computed(() => {
    let tradeInSale: string | number =
        typeof HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale === "object"
            ? HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale?.tradeInSale
            : HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale;
    if (tradeInSale) {
        tradeInSale = parseStringPrice(tradeInSale);
    } else {
        tradeInSale = 0;
    }

    const priceWithRabat = TIWStore.HOLD
        ? tradeInSale
        : Number(((tradeInSale / 100) * 70).toFixed());
    const finalPrice =
        priceWithRabat + (HowYouRateDeviceStore.registeringDeviceData.additionalDiscountValue ?? 0);

    // FIXME: добавь грамотную обработку цены без приведения типов туда-сюда.
    return String(finalPrice);
});

const voucherText = useTranslate(
    `tiw__voucherSent__text${additionalCertificateStringEndingForTranslation.value}`,
)
    .replace(
        "[[PRICE]]",
        devicePrice.value, // FIXME: см. fixme выше.
    )
    .replace("[[CURRENCY]]", HowYouRateDeviceStore.registeringDeviceData.currency);

const loadingToNext = ref<boolean>(false);
</script>

<template>
    <Modal class="tiw__step__voucherSent">
        <template #header>
            <p class="tiw__step__voucherSent__header tiw__h2">
                <AwardIcon />
                {{
                    useTranslate(
                        `tiw__voucherSent__title${additionalCertificateStringEndingForTranslation}`,
                    )
                }}
            </p>
            <p>
                <span v-html="voucherText" />
            </p>
        </template>
        <div class="tiw__step__voucherSent__body">
            <VoucherNumber
                :certificate-number="TIWStore.CERTIFICATE_NUMBER"
                :is-loading="loadingToNext"
            />
            <VoucherNumber
                v-for="additionalCertificate of TIWStore.ADDITIONAL_CERTIFICATE_NUMBERS"
                :key="additionalCertificate"
                :certificate-number="additionalCertificate"
                :is-loading="loadingToNext"
            />

            <div class="tiw__step__voucherSent__body__whatNext">
                <p>{{ useTranslate("tiw__voucherSent__whatNext__title") }}</p>
                <ul>
                    <li>{{ useTranslate("tiw__voucherSent__whatNext__goToOrange") }}</li>
                    <li>
                        {{
                            useTranslate(
                                `tiw__voucherSent__whatNext__enterVoucher${additionalCertificateStringEndingForTranslation}`,
                            )
                        }}
                    </li>
                    <li>{{ useTranslate("tiw__voucherSent__whatNext__finalizeYourOrder") }}</li>
                    <li>{{ useTranslate("tiw__voucherSent__whatNext__14days") }}</li>
                </ul>
            </div>
            <a href="https://www.orange.pl/esklep/smartfony?typ-oferty=VOICE&tylko-dostepne">
                <Button
                    type="button"
                    :disabled="loadingToNext"
                    :loading="loadingToNext"
                    class="tiw__step__voucherSent__btn-send"
                >
                    <span>
                        {{
                            useTranslate(
                                `tiw__voucherSent__whatNext__useVoucherNow${additionalCertificateStringEndingForTranslation}`,
                            )
                        }}
                    </span>
                </Button>
            </a>
        </div>
    </Modal>
</template>

<style lang="scss" src="./voucherSent.scss"></style>
